import React, { useEffect, useState } from "react";
import { CreateNewTrade, GetBotOrders, TradeType } from "./mmTypes";
import "../../index.css";
import { useAlertService } from "../../hooks/useAlertService";
import { formatOrderTimestamp } from "./orderTime";

const orderSource = (source?: string) => source === "spread_skip" ? "Spread skip" : source === "mm_pair" ? "MM pair" : "Unknown";

const ExecutionDetails = ({ fills }: { fills?: any[] | null }) => (
  <div style={{ overflowWrap: "anywhere" }}>
    {fills?.length ? fills.map((fill) => (
      <div key={fill.trade_id} style={{ marginBottom: 6 }}>
        <div>{formatOrderTimestamp(fill.executed_at, fill.timestamp_ms)}</div>
        <div>{fill.amount} @ {fill.price} {fill.role ? `(${fill.role})` : ""}</div>
        {fill.fee != null && <div>Fee: {fill.fee} {fill.fee_currency}</div>}
        <small>Trade ID: {fill.trade_id}</small>
      </div>
    )) : "—"}
  </div>
);

interface TradingSettings {
  id: string;
  exchange: string;
  open_key: string;
  private_key: string;
  password: string;
}

interface TradingSettingsProps {
  data: TradingSettings;
  visState: boolean;
}
const getLastTrade = async (
  connectionId: string,
  publicKey: string,
  privateKey: string,
  exchange: string,
  password: string
) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/liquid_bots/" +
      connectionId +
      "/get_last_order?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      privateKey +
      "&password=" +
      password,
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
    }
  );

  return await res.json();
};

const createSingleTrade = async (
  connectionId: string,
  publicKey: string,
  privateKey: string,
  exchange: string,
  body: any,
  password: string
) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/liquid_bots/" +
      connectionId +
      "/create_order?trading_sdk_type=" +
      exchange +
      "&public_key=" +
      publicKey +
      "&secret_key=" +
      privateKey +
      "&password=" +
      password,
    {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );

  if (res.status !== 200) {
    throw new Error("Unable to create order");
  }
  return await res.json();
};
const createMultiTrade = async () => {};

const getBotsOrders = async (connectionId: string) => {
  const res = await fetch(
    "https://lukkypro.ru/api/v1/bots/" + connectionId + "/orders?limit=50",
    {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
        "Content-Type": "application/json",
      },
    }
  );

  return await res.json();
};

const ismobile =
/Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
  navigator.userAgent
);

const OpenTrade: React.FC<TradingSettingsProps> = (props) => {
  const [lastTradeData, setLastTradeData] = useState<CreateNewTrade>();
  const [botOrders, setBotOrders] = useState<GetBotOrders>();
  const [sortedOrders, setSortedOrders] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [amount, setAmount] = useState<number>();
  const [price, setPrice] = useState<number>();
  const [pair, setPair] = useState<string>();
  const [side, setSide] = useState<TradeType>(TradeType.BUY);
  const [amountOfTrades, setAmountOfTrades] = useState(1);
  const { showAlert } = useAlertService();

  const sortOrdersByDate = (orders: any[], order: "asc" | "desc") => {
    return [...orders].sort((a, b) => {
      const dateA = new Date(a.exchange_created_at || `${a.created_at.replace(/Z$/, "")}Z`);
      const dateB = new Date(b.exchange_created_at || `${b.created_at.replace(/Z$/, "")}Z`);
      return order === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    });
  };

  const handleSortToggle = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);
    if (botOrders?.orders) {
      setSortedOrders(sortOrdersByDate(botOrders.orders, newSortOrder));
    }
  };

  const formatNumber = (value: string | number): string => {
    const num = parseFloat(value.toString());
    return isNaN(num) ? value.toString() : num.toString();
  };

  const fetchBotOrders = async () => {
    try {
      const lastBotOrders = (await getBotsOrders(
        props.data.id
      )) as GetBotOrders;
      setBotOrders(lastBotOrders);
      setSortedOrders(sortOrdersByDate(lastBotOrders.orders || [], sortOrder));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const lastTrade = (await getLastTrade(
          props.data.id,
          props.data.open_key,
          props.data.private_key,
          props.data.exchange,
          props.data.password
        )) as CreateNewTrade;

        setAmount(lastTrade.amount);
        setPrice(lastTrade.price);
        setPair(lastTrade.pair);
        setSide(lastTrade.is_buy);
        setLastTradeData(lastTrade);

        await fetchBotOrders();
      } catch (error) {
        console.log(error);
      }
    })();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchBotOrders();
    }, 60000);

    return () => clearInterval(interval);
  }, [sortOrder]);

  const [isMobile, setMobile] = useState(ismobile);

  useEffect(() => {
    const mobile =
      /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
        navigator.userAgent
      );
    setMobile(mobile);
  }, []);

  const inputHandler = (e: any) => {
    console.log(e.target.id);
    //@ts-ignore
    lastTradeData[e.target.id] = e.target.value;
    setLastTradeData(lastTradeData);
  };

  const handleAddTrade = (e: any) => {
    e.preventDefault();
    setAmountOfTrades(amountOfTrades + 1);
  };

  const handleRemoveTrade = (e: any) => {
    e.preventDefault();
    if (amountOfTrades > 1) {
      setAmountOfTrades(amountOfTrades - 1);
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPrice(value === "" ? undefined : +value);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmount(value === "" ? undefined : +value);
  };

  const handleSendButton = async (e: any) => {
    e.preventDefault();
    try {
      if (amountOfTrades > 1) {
        return await createMultiTrade();
      }
      const data = {
        amount,
        pair,
        price,
        side,
      };

      await createSingleTrade(
        props.data.id,
        props.data.open_key,
        props.data.private_key,
        props.data.exchange,
        {
          amount,
          pair,
          price,
          is_buy: side,
        },
        props.data.password
      );

      showAlert({ type: "success", message: "Order executed successfully!" });
    } catch (error) {
      console.error("Error executing order:", error);
      showAlert({
        type: "error",
        message: "Failed to execute order. Please try again.",
      });
    }
  };

  return (
    <div
      style={{
        display: props.visState ? "flex" : "none",
        flexDirection: props.visState ? "column" : "unset",
      }}
    >
      {/* Bot Orders Display */}
      {botOrders && botOrders.orders && botOrders.orders.length > 0 && (
        <div style={ordersContainerStyle}>

          {isMobile ? (
            <div style={mobileCardsContainerStyle}>
              {sortedOrders.map((order) => (
                <div key={order.id} style={mobileCardStyle}>
                  {order.risk_details && <div style={{ color: "#b91c1c" }}>Защитная пауза: {order.risk_details.reason}. Изменение токенов: {order.risk_details.base_delta_before_fees ?? "не подтверждено"}</div>}
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Order price:</span>
                    <span
                      style={{
                        ...mobileCardValueStyle,
                        color:
                          order.order_side === "BUY" ? "#22c55e" : "#ef4444",
                        fontWeight: "bold",
                      }}
                    >
                      {formatNumber(order.price)}
                    </span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Order amount:</span>
                    <span style={mobileCardValueStyle}>
                      {formatNumber(order.amount)}
                    </span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Fill price:</span>
                    <span style={mobileCardValueStyle}>
                      {order.executed_price != null ? formatNumber(order.executed_price) : "—"}
                    </span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Filled amount:</span>
                    <span style={mobileCardValueStyle}>
                      {order.executed_amount != null ? formatNumber(order.executed_amount) : "Unconfirmed"}
                    </span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Status:</span>
                    <span style={mobileCardValueStyle}>{order.exchange_status || "Unconfirmed"}</span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Source:</span>
                    <span style={mobileCardValueStyle}>{orderSource(order.order_source)}</span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Order ID:</span>
                    <span style={{ ...mobileCardValueStyle, overflowWrap: "anywhere" }}>{order.exchange_id}</span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>{order.exchange_created_at ? "Exchange created:" : "Recorded at:"}</span>
                    <span style={mobileCardValueStyle}>
                      {formatOrderTimestamp(order.exchange_created_at || order.created_at)}
                    </span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Exchange updated:</span>
                    <span style={mobileCardValueStyle}>{formatOrderTimestamp(order.exchange_updated_at)}</span>
                  </div>
                  <div style={mobileCardRowStyle}>
                    <span style={mobileCardLabelStyle}>Executions:</span>
                    <div style={mobileCardValueStyle}><ExecutionDetails fills={order.execution_details} /></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={tableContainerStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={tableHeaderCellStyle}>Order price</th>
                    <th style={tableHeaderCellStyle}>Order amount</th>
                    <th style={tableHeaderCellStyle}>Fill price</th>
                    <th style={tableHeaderCellStyle}>Filled amount</th>
                    <th style={tableHeaderCellStyle}>Status</th>
                    <th style={tableHeaderCellStyle}>Source / Order ID</th>
                    <th style={tableHeaderCellStyle}>Created at (exchange / recorded)</th>
                    <th style={tableHeaderCellStyle}>Exchange updated</th>
                    <th style={tableHeaderCellStyle}>Executions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOrders.map((order) => (
                    <tr key={order.id}>
                      <td
                        style={{
                          ...tableCellStyle,
                          color:
                            order.order_side === "BUY" ? "#22c55e" : "#ef4444",
                          fontWeight: "bold",
                        }}
                      >
                        {formatNumber(order.price)}
                      </td>
                      <td style={tableCellStyle}>{formatNumber(order.amount)}</td>
                      <td style={tableCellStyle}>{order.executed_price != null ? formatNumber(order.executed_price) : "—"}</td>
                      <td style={tableCellStyle}>{order.executed_amount != null ? formatNumber(order.executed_amount) : "Unconfirmed"}</td>
                      <td style={tableCellStyle}>{order.exchange_status || "Unconfirmed"}{order.risk_details && <div style={{ color: "#b91c1c" }}>Защита: {order.risk_details.reason}</div>}</td>
                      <td style={tableCellStyle}>{orderSource(order.order_source)}<br />{order.exchange_id}</td>
                      <td style={tableCellStyle}>
                        {formatOrderTimestamp(order.exchange_created_at || order.created_at)}
                      </td>
                      <td style={tableCellStyle}>{formatOrderTimestamp(order.exchange_updated_at)}</td>
                      <td style={tableCellStyle}><ExecutionDetails fills={order.execution_details} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {lastTradeData ? (
        <form style={formStyle}>
          {[...Array(amountOfTrades)].map((e, i) => (
            <div id={i.toString()} key={i.toString()}>
              <div style={inputContainerStyle}>
                <label htmlFor="pair">Pair</label>
                <input
                  onChange={(e) => {
                    setPair(e.target.value);
                  }}
                  id="pair"
                  type="text"
                  disabled
                  value={pair}
                />
              </div>
              <div style={inputContainerStyle}>
                <label htmlFor="price">Price</label>
                <input
                  onChange={handlePriceChange}
                  id="price"
                  type="number"
                  value={price ?? ""}
                />
              </div>
              <div style={inputContainerStyle}>
                <label htmlFor="amount">Amount</label>
                <input
                  onChange={handleAmountChange}
                  id="amount"
                  type="number"
                  value={amount ?? ""}
                />
              </div>
              <div style={inputContainerStyle}>
                <div className="customCheckBoxHolder">
                  <input
                    className="customCheckBoxInput"
                    id="cCB1"
                    type="checkbox"
                    checked={side === TradeType.BUY}
                  />
                  <label className="customCheckBoxWrapper" htmlFor="cCB1">
                    <div
                      className="customCheckBox"
                      onClick={() => setSide(TradeType.BUY)}
                    >
                      <div className="inner">Buy</div>
                    </div>
                  </label>

                  <input
                    className="customCheckBoxInputSell"
                    id="cCB2"
                    type="checkbox"
                    checked={side === TradeType.SELL}
                  />
                  <label
                    style={{ marginLeft: "auto" }}
                    className="customCheckBoxWrapper"
                    htmlFor="cCB2"
                  >
                    <div
                      className="customCheckBox"
                      onClick={() => setSide(TradeType.SELL)}
                    >
                      <div className="inner">Sell</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          ))}
          {/* <button
            onClick={(e) => {
              handleAddTrade(e);
            }}
          >
            Add trade
          </button> */}
          {/* <button
            onClick={(e) => {
              handleRemoveTrade(e);
            }}
          >
            Remove trade
          </button> */}
          <button
          style={{
            width: '100%',
            height: '40px',
            color: 'black',
            // backgroundColor: 'grey',
            margin: 'auto',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            textAlign: 'center',
          }}
            onClick={(e) => {
              handleSendButton(e);
            }}
          >
            Send to execution
          </button>
        </form>
      ) : (
        <div></div>
      )}
    </div>
  );
};

const formStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "1rem",
};

const inputContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};

const ordersContainerStyle: React.CSSProperties = {
  marginTop: "2rem",
  padding: "1rem",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  backgroundColor: "#f9fafb",
};

const tableHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "1rem",
  padding: "0.5rem 0",
  borderBottom: "1px solid #e5e7eb",
};

const sortButtonStyle: React.CSSProperties = {
  padding: "0.5rem 1rem",
  backgroundColor: "transparent",
  color: "inherit",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "0.875rem",
  fontWeight: "500",
};

const tableContainerStyle: React.CSSProperties = {
  width: "auto",
  height: "350px",
  overflowY: "auto",
  overflowX: "auto",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  backgroundColor: "white",
};

const tableHeaderCellStyle: React.CSSProperties = {
  padding: "0.75rem",
  textAlign: "center",
  fontWeight: "600",
  borderBottom: "1px solid #d1d5db",
  backgroundColor: "#f9fafb",
};

const tableCellStyle: React.CSSProperties = {
  padding: "0.75rem",
  textAlign: "center",
  borderBottom: "1px solid #e5e7eb",
};

const tableHeaderStyleMobile: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  marginBottom: "1rem",
  padding: "0.5rem 0",
  borderBottom: "1px solid #e5e7eb",
};

const sortButtonStyleMobile: React.CSSProperties = {
  padding: "0.5rem 1rem",
  backgroundColor: "transparent",
  color: "inherit",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "0.875rem",
  fontWeight: "500",
  width: "100%",
};

const mobileCardsContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  maxHeight: "350px",
  overflowY: "auto",
};

const mobileCardStyle: React.CSSProperties = {
  padding: "1rem",
  backgroundColor: "white",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

const mobileCardRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.25rem 0",
};

const mobileCardLabelStyle: React.CSSProperties = {
  fontWeight: "600",
  fontSize: "0.875rem",
  color: "#6b7280",
};

const mobileCardValueStyle: React.CSSProperties = {
  fontSize: "0.875rem",
  textAlign: "right",
  wordBreak: "break-word",
};

export default OpenTrade;
